import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Package, ChevronRight, ShoppingBag } from "lucide-react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingButtons from "../components/FloatingButtons";
import PageBanner from "../components/PageBanner";
import { useCustomerAuth } from "../lib/CustomerAuthContext";

const STATUS_LABELS = {
  pending: "Alındı",
  preparing: "Hazırlanıyor",
  ready: "Hazır",
  completed: "Tamamlandı",
  cancelled: "İptal Edildi",
};

const PAYMENT_LABELS = {
  unpaid: "Kapıda/Elden Ödeme",
  pending: "Ödeme Bekleniyor",
  paid: "Ödendi",
  failed: "Ödeme Başarısız",
  refunded: "İade Edildi",
};

export default function OrdersHistory() {
  const { isLoggedIn, loading: authLoading, fetchOrders } = useCustomerAuth();
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isLoggedIn) return;
    let cancelled = false;
    fetchOrders().then((res) => {
      if (cancelled) return;
      if (res.ok) setOrders(res.orders);
      else setError(res.error);
    });
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, fetchOrders]);

  if (!authLoading && !isLoggedIn) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-white text-[#222222]" data-testid="orders-history-page">
      <Header />

      <PageBanner
        eyebrow="Hesabım"
        title="Siparişlerim"
        subtitle="Geçmiş siparişlerinizi ve durumlarını buradan takip edebilirsiniz."
        breadcrumb={[{ label: "Siparişlerim" }]}
      />

      <section className="py-16 md:py-24 bg-white">
        <div className="frida-container max-w-3xl">
          {error && (
            <p className="text-center text-sm text-red-600 mb-6">{error}</p>
          )}

          {orders === null && !error ? (
            <p className="text-center text-sm text-[#888888] py-10">Yükleniyor...</p>
          ) : orders?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="orders-empty">
              <div className="w-16 h-16 rounded-full bg-[#F8F8F8] flex items-center justify-center mb-5">
                <ShoppingBag className="w-7 h-7 text-[#888888]" />
              </div>
              <h3 className="font-serif text-xl mb-2">Henüz siparişiniz yok</h3>
              <p className="text-sm text-[#888888] max-w-xs mb-6">
                Verdiğiniz siparişler bu hesapla giriş yaptığınızda burada görünür.
              </p>
              <Link
                to="/urunler"
                className="px-7 py-3.5 bg-[#A85C34] hover:bg-[#8B4726] text-white rounded-full font-medium transition-colors"
              >
                Alışverişe Başla
              </Link>
            </div>
          ) : (
            <div className="space-y-4" data-testid="orders-list">
              {orders?.map((o) => (
                <div key={o.id} className="rounded-2xl border border-[#EAEAEA] p-5 md:p-6" data-testid={`order-card-${o.id}`}>
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#F8F8F8] flex items-center justify-center shrink-0">
                        <Package className="w-4 h-4 text-[#A85C34]" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Sipariş #{o.id}</p>
                        <p className="text-xs text-[#888888]">
                          {new Date(o.created_at).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-serif text-lg font-bold">{o.total_price.toLocaleString("tr-TR")}₺</p>
                      <span className="text-[10px] font-semibold tracking-wide uppercase px-2.5 py-1 rounded-full bg-[#F5E9DD] text-[#A85C34] inline-block mt-1">
                        {STATUS_LABELS[o.status] || o.status}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-1 mb-3">
                    {o.items.map((it, i) => (
                      <div key={i} className="flex items-center justify-between text-sm text-[#555555]">
                        <span>{it.quantity}x {it.name}</span>
                        <span>{(it.quantity * it.price).toLocaleString("tr-TR")}₺</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-[#EAEAEA] text-xs text-[#888888]">
                    <span className="truncate max-w-[70%]">{o.delivery_address || "—"}</span>
                    <span className="inline-flex items-center gap-1 shrink-0">
                      {PAYMENT_LABELS[o.payment_status] || o.payment_status}
                      <ChevronRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
}
